/**
 * LocalDiffBadge — sits in the DevConsole toolbar. Every 60s (and on demand)
 * calls /api/dev-console/sync/local-diff-summary to see how many files in the
 * dev pod differ from GitHub main. Click → dialog with checklist + "Push
 * selected" button that ships them as an atomic commit with [skip auto-revert].
 * v3.4 (2026-07-02)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { GitPullRequest, RefreshCw, Loader2, CheckSquare, Square, Rocket, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = API + '/api/dev-console/sync';
const POLL_INTERVAL_MS = 60_000;

export function LocalDiffBadge() {
  const [summary, setSummary] = useState(null); // {counts, ahead[], gh_main_head}
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [bumpVersion, setBumpVersion] = useState(true);
  const timerRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(BASE + '/local-diff-summary', { timeout: 60000 });
      setSummary(r.data);
    } catch (e) {
      // Silently ignore 403 (non-super-admin) — badge just stays hidden
      if (e.response?.status !== 403) {
        console.warn('[LocalDiffBadge] fetch failed:', e?.response?.data?.detail || e.message);
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    timerRef.current = setInterval(fetchSummary, POLL_INTERVAL_MS);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [fetchSummary]);

  const openDialog = () => {
    setPushResult(null);
    // Preselect everything
    setSelected(new Set((summary?.ahead || []).map((f) => f.path)));
    setOpen(true);
  };

  const toggleOne = (path) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === (summary?.ahead || []).length) setSelected(new Set());
    else setSelected(new Set((summary?.ahead || []).map((f) => f.path)));
  };

  const doPush = async () => {
    if (selected.size === 0) { toast.error('Select at least one file'); return; }
    setPushing(true);
    setPushResult(null);
    try {
      const r = await axios.post(BASE + '/push-local-diff', {
        paths: Array.from(selected),
        bump_version: bumpVersion,
      }, { timeout: 120_000 });
      setPushResult(r.data);
      if (r.data.ok) {
        toast.success(r.data.message);
      } else {
        toast.error(`Pushed ${r.data.pushed_count}, ${r.data.failed_count} failed — see dialog`);
      }
      // Refresh the summary after push
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Push failed');
    } finally {
      setPushing(false);
    }
  };

  // If not super-admin OR the endpoint 403s, don't render at all
  if (!summary) return null;
  const total = summary.counts?.ahead_total || 0;
  if (total === 0 && !open) {
    // Still show as a tiny green tick so the user knows sync is healthy
    return (
      <button
        onClick={openDialog}
        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/40 h-8 text-xs gap-1 px-2 rounded flex items-center transition"
        title={`In sync with GitHub main @ ${summary.gh_main_head}`}
        data-testid="local-diff-badge"
      >
        <GitPullRequest className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">In sync</span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={openDialog}
        className="text-amber-300 hover:text-amber-200 hover:bg-amber-950/40 h-8 text-xs gap-1 px-2 rounded flex items-center transition animate-pulse"
        title={`${total} local file(s) differ from GitHub main. Click to review + push.`}
        data-testid="local-diff-badge"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        <span className="font-semibold">{total}</span>
        <span className="hidden sm:inline">ahead</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="local-diff-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <GitPullRequest className="h-4 w-4 text-amber-400" />
              Local files ahead of GitHub main
              {summary.gh_main_head && <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-400 ml-1 font-mono">main @ {summary.gh_main_head}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {pushResult ? (
            <div className="space-y-3">
              <div className={`rounded p-2 text-sm ${pushResult.ok ? 'bg-emerald-950/40 border border-emerald-800 text-emerald-200' : 'bg-amber-950/40 border border-amber-800 text-amber-200'}`}>
                {pushResult.message}
              </div>
              {pushResult.pushed?.length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-emerald-300 font-medium">✅ Pushed ({pushResult.pushed.length})</summary>
                  <div className="mt-1 max-h-40 overflow-y-auto font-mono text-[11px] text-zinc-400 space-y-0.5">
                    {pushResult.pushed.map((p, i) => (
                      <div key={i}>{p.path} <span className="text-zinc-600">→ {p.commit}</span></div>
                    ))}
                  </div>
                </details>
              )}
              {pushResult.failed?.length > 0 && (
                <details className="text-xs" open>
                  <summary className="cursor-pointer text-rose-300 font-medium">❌ Failed ({pushResult.failed.length})</summary>
                  <div className="mt-1 max-h-40 overflow-y-auto font-mono text-[11px] text-rose-200/70 space-y-0.5">
                    {pushResult.failed.map((f, i) => (
                      <div key={i}><span className="text-rose-300">{f.path}</span> — {f.reason}</div>
                    ))}
                  </div>
                </details>
              )}
              {pushResult.new_version && (
                <div className="text-[11px] text-zinc-500">
                  Platform version bumped to <code className="text-cyan-400">v{pushResult.new_version}</code>.
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-xs">
                <div className="text-zinc-400">
                  <button onClick={toggleAll} className="inline-flex items-center gap-1 text-zinc-300 hover:text-white" data-testid="local-diff-toggle-all">
                    {selected.size === (summary.ahead || []).length ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                    {selected.size} / {summary.ahead.length} selected
                  </button>
                  <span className="ml-3 text-zinc-500">
                    (<span className="text-amber-400">{summary.counts.modified}</span> modified,
                    {' '}<span className="text-emerald-400">{summary.counts.created}</span> new)
                  </span>
                </div>
                <Button size="sm" variant="ghost" onClick={fetchSummary} disabled={loading} className="text-zinc-300 h-7" data-testid="local-diff-refresh">
                  {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                </Button>
              </div>

              <div className="border border-zinc-800 rounded max-h-[45vh] overflow-y-auto bg-zinc-950/60">
                {(summary.ahead || []).map((f) => (
                  <label
                    key={f.path}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-800/40 cursor-pointer text-xs border-b border-zinc-900 last:border-b-0"
                    data-testid={`local-diff-row-${f.path.replace(/[^a-z0-9]/gi, '_').slice(0, 40)}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(f.path)}
                      onChange={() => toggleOne(f.path)}
                      className="accent-emerald-500"
                    />
                    <Badge variant="outline" className={
                      'text-[9px] shrink-0 ' +
                      (f.kind === 'created' ? 'border-emerald-700 text-emerald-400' : 'border-amber-700 text-amber-400')
                    }>{f.kind === 'created' ? 'new' : 'mod'}</Badge>
                    <code className="text-zinc-300 truncate flex-1">{f.path}</code>
                    <span className="text-zinc-500 shrink-0">{(f.size / 1024).toFixed(1)}KB</span>
                  </label>
                ))}
                {(!summary.ahead || summary.ahead.length === 0) && (
                  <div className="p-6 text-center text-xs text-zinc-500">Nothing ahead of main.</div>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={bumpVersion} onChange={(e) => setBumpVersion(e.target.checked)} className="accent-emerald-500" />
                Auto-bump <code className="text-cyan-400">version.js</code> patch (recommended — forces Plesk to rebuild)
              </label>
            </>
          )}

          <DialogFooter className="flex-row gap-2 justify-end">
            {pushResult ? (
              <Button onClick={() => { setOpen(false); setPushResult(null); }} data-testid="local-diff-close">Close</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} disabled={pushing} data-testid="local-diff-cancel">Cancel</Button>
                <Button
                  onClick={doPush}
                  disabled={pushing || selected.size === 0}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="local-diff-push"
                >
                  {pushing ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Pushing…</> : <><Rocket className="h-3 w-3 mr-1" /> Push {selected.size} to main</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
